import { useEffect, useRef, useState, type ReactNode } from "react";
import "./styles/EnterGate.css";

interface EnterGateProps {
  children: ReactNode;
  buttonText?: string;
  musicSrc?: string;
  musicVolume?: number; // 0 to 1
  fadeDurationMs?: number;
  apologyMessage?: string;
  typingSpeedMs?: number; // ms per character
}

const EnterGate: React.FC<EnterGateProps> = ({
  children,
  buttonText = "Enter",
  musicSrc,
  musicVolume = 1,
  fadeDurationMs = 600,
  apologyMessage,
  typingSpeedMs = 45,
}) => {
  const [entered, setEntered] = useState(false);
  const [gateVisible, setGateVisible] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);
  const [typedLength, setTypedLength] = useState(0);
  const [showButton, setShowButton] = useState(!apologyMessage);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Types the apology out one character at a time, then reveals the
  // button after a short pause — so she has to actually read it first.
  useEffect(() => {
    if (!apologyMessage) return;

    if (typedLength >= apologyMessage.length) {
      const revealButton = setTimeout(() => setShowButton(true), 700);
      return () => clearTimeout(revealButton);
    }

    const id = setTimeout(() => setTypedLength((n) => n + 1), typingSpeedMs);
    return () => clearTimeout(id);
  }, [apologyMessage, typedLength, typingSpeedMs]);

  const handleEnter = () => {
    // Called directly inside a real click handler, so browsers treat
    // this as user-initiated and won't block it like onload autoplay.
    const audio = audioRef.current;
    if (audio) {
      audio.volume = musicVolume;
      audio.play().catch(() => {});
    }

    setGateVisible(false); // start fading the gate out

    setTimeout(() => {
      setEntered(true);
      // let the content mount hidden, then fade it in on the next tick
      requestAnimationFrame(() => setContentVisible(true));
    }, fadeDurationMs);
  };

  const isTyping = !!apologyMessage && typedLength < apologyMessage.length;

  return (
    <>
      {musicSrc && <audio ref={audioRef} src={musicSrc} loop />}

      {!entered && (
        <div
          className="enter-gate"
          style={{
            opacity: gateVisible ? 1 : 0,
            transitionDuration: `${fadeDurationMs}ms`,
          }}
        >
          {apologyMessage && (
            <p className="enter-gate-apology">
              {apologyMessage.slice(0, typedLength)}
              {isTyping && <span className="typing-cursor">|</span>}
            </p>
          )}
          <button
            className={`enter-gate-button${showButton ? " enter-gate-button--visible" : ""}`}
            onClick={handleEnter}
          >
            {buttonText}
          </button>
        </div>
      )}

      {entered && (
        <div
          className="enter-gate-content"
          style={{
            opacity: contentVisible ? 1 : 0,
            transitionDuration: `${fadeDurationMs}ms`,
          }}
        >
          {children}
        </div>
      )}
    </>
  );
};

export default EnterGate;